import React, { useState, useEffect } from 'react';
import { Modal, Button, TextInput, Label, Select, FileInput, Textarea, Spinner } from 'flowbite-react';
import { Storage, Auth } from 'aws-amplify';
import { X } from 'lucide-react';
import { toast } from 'react-toastify';
import { FiLoader } from 'react-icons/fi';
const ItemModal = ({
  showModal,
  setShowModal,
  currentItem,
  isEditMode,
  handleSave,
  institution,
  categories,
  currencies,
  setItemData,
  itemData
}) => {
  const [descriptionError, setDescriptionError] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [isOtherSelected, setIsOtherSelected] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);
  const [imageError, setImageError] = useState(null);
  const [selectedFoodType, setSelectedFoodType] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (showModal) {
      setIsOtherSelected(false);
      setImageError(null);
      setSelectedFoodType(null);
      setIsSubmitting(false);
    }
    if (isEditMode && currentItem) {
      setItemData({
        institution,
        itemName: currentItem.itemName,
        price: currentItem.price,
        veg: currentItem.veg,
        category: currentItem.category || '',
        currency: currentItem.currency || 'INR',
        outOfStock: currentItem.outOfStock || false,
        description: currentItem.description || '',
        imageUrl: currentItem.imageUrl || null,
      });
      setSelectedFoodType(currentItem.veg === true ? 'veg' : currentItem.veg === false ? 'non-veg' : null);
      setImagePreview(currentItem.imageUrl);
    }

    if (!isEditMode) {
      setItemData({
        itemName: '',
        price: 0,
        veg: undefined,
        category: 'Select',
        currency: 'INR',
        outOfStock: false,
        institution,
        description: '',
        itemImage: null,
        imageUrl: null,
      });
      setSelectedFoodType(null);
      setImagePreview(null);
    }
  }, [currentItem, isEditMode, showModal]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    const updatedValue = name === 'price' ? parseFloat(value) || 0 : value;
    if (name === 'price' && updatedValue < 0) return;
    setItemData((prevData) => ({ ...prevData, [name]: updatedValue }));
  };

  const handleCategoryChange = (e) => {
    const { value } = e.target;
    if (value === 'Other') {
      setItemData((prevData) => ({ ...prevData, category: '' }));
      setIsOtherSelected(true);
    } else {
      setItemData((prevData) => ({ ...prevData, category: value }));
      setIsOtherSelected(false);
    }
  };

  const handleImageChange = async (event) => {
    const file = event.target.files[0];
    setImageError(null);
    setUploading(true);

    try {
      if (file) {
        const allowedExtensions = ['png', 'jpg', 'jpeg', 'svg'];
        const fileExtension = file.name.split('.').pop().toLowerCase();
  
        if (!allowedExtensions.includes(fileExtension)) {
          throw new Error('Invalid file format. Only PNG, JPG, JPEG, and SVG are allowed.');
        }
  
        if (file.size > 5 * 1024 * 1024) {
          throw new Error('Image size must be less than 5MB');
        }

        const currentUser = await Auth.currentAuthenticatedUser();
        const cognitoId = currentUser.attributes.sub;

        const timestamp = new Date().getTime();
        const filename = `${institution}/${cognitoId}/items/${timestamp}-${file.name}`;

        const uploadResult = await Storage.vault.put(
          filename,
          file,
          {
            contentType: file.type,
            level: 'public',
            region: 'us-east-1',
            ACL: 'public-read'
          }
        );

        let imageUrl = await Storage.get(uploadResult.key);
        imageUrl = imageUrl.split('?')[0];

        setImagePreview(imageUrl);
        setItemData(prev => ({ ...prev, imageUrl }));
      }
    } catch (error) {
      console.error('Error processing image:', error);
      setImageError(error.message || 'Error processing image. Please try again.');
      setItemData(prev => ({ ...prev, imageUrl: null }));
      setImagePreview(null);
    } finally {
      setUploading(false);
    }
  };

  const handleFoodTypeChange = (value) => {
    setSelectedFoodType(value);
    setItemData((prevData) => ({ ...prevData, veg: value === 'veg' }));
  };

  const validateForm = () => {
    if (!itemData.itemName.trim()) {
      toast.info('Please enter an item name');
      return false;
    }
    
    if (itemData.price <= 0) {
      toast.info('Price must be greater than zero');
      return false;
    }
    
    if (selectedFoodType === null) {
      toast.info('Please select food type (Veg/Non-veg)');
      return false;
    }
    
    if (!itemData.category || itemData.category === 'Select') {
     toast.info('Please select or enter a category');
      return false;
    }
    
    return true;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;
    
    setIsSubmitting(true);
    try {
     
      await handleSave(itemData);
     
    } catch (error) {
      console.error('Error saving item:', error);
      toast.error('Failed to save item: ' + (error.message || 'Unknown error'));
    } finally {
      setIsSubmitting(false);
    
    }
  };

  const closeModal = () => {
    if (isSubmitting) {
      return;
    }
    setShowModal(false);
    setIsOtherSelected(false);
    setSelectedFoodType(null);
  };

  const isFormValid = () => {
    return (
      itemData.itemName.trim() &&
      itemData.price > 0 &&
      selectedFoodType !== null &&
      itemData.category &&
      itemData.category !== 'Select'
    );
  };

  return (
    <Modal 
      show={showModal} 
      size="lg" 
      onClose={closeModal} 
      popup
      dismissible={!isSubmitting} 
    >
      <div className="relative">
        <button
          onClick={closeModal}
          disabled={isSubmitting}
          className={`absolute right-4 top-4 text-gray-500 ${
            isSubmitting ? 'opacity-50 cursor-not-allowed' : 'hover:text-gray-700'
          }`}
        >
          <X size={24} />
        </button>
        <div className="flex flex-row justify-center">
          <h2 className="text-xl font-semibold mt-6">
            {isEditMode ? 'Edit Item' : 'Add Item'}
          </h2>
        </div>
      </div>
      {/* {isSubmitting && (
        <div className="absolute inset-0 bg-white bg-opacity-70 flex items-center justify-center z-50 rounded-lg">
          <div className="text-center p-6 bg-white rounded-lg shadow-lg">
            <div className="w-12 h-12 border-4 border-primaryColor border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-lg font-medium text-gray-700">
              {isEditMode ? 'Saving changes...' : 'Adding item...'}
            </p>
            <p className="text-sm text-gray-500 mt-2">
              Please wait while we update the database
            </p>
          </div>
        </div>
      )} */}
      <Modal.Body>
        <div className="px-4 py-2">
          <div className="mb-4">
            <Label htmlFor="itemName" value="Item Name *" className="mb-1" />
            <TextInput
              id="itemName"
              name="itemName"
              placeholder="Enter the item name"
              value={itemData.itemName || ''}
              onChange={(e) => {
                const value = e.target.value.replace(/[^a-zA-Z0-9\s]/g, '').slice(0, 100);
                handleInputChange({ target: { name: 'itemName', value } });
              }}
              required
              disabled={isSubmitting}
            />
          </div>

          <div className="mb-4">
            <Label htmlFor="description" value="Description (Optional)" className="mb-1" />
            <Textarea
              id="description"
              name="description"
              placeholder="Enter item description"
              value={itemData.description || ''}
              onChange={handleInputChange}
              rows={3}
              disabled={isSubmitting}
            />
          </div>

          <div className="mb-4">
            <Label htmlFor="itemImage" value="Item Image (Optional)" className="mb-1" />
            <FileInput
              id="itemImage"
              accept="image/*"
              onChange={handleImageChange}
              helperText="Maximum file size: 5MB"
              disabled={uploading || isSubmitting}
            />
            {uploading && (
              <div className="flex items-center mt-2 text-sm text-blue-600">
                <Spinner size="sm" className="mr-2" />
                Uploading image...
              </div>
            )}
            {imageError && (
              <p className="text-red-500 text-sm mt-1">{imageError}</p>
            )}
            {imagePreview && (
              <div className="mt-2 flex items-end gap-2">
                <img
                  src={imagePreview}
                  alt="Item preview"
                  className="w-32 h-32 object-cover rounded-lg border border-gray-200"
                />
                {!isSubmitting && (
                  <button
                    onClick={() => {
                      setImagePreview(null);
                      setItemData(prev => ({ ...prev, imageUrl: null }));
                    }}
                    className="text-red-500 text-sm mb-2 hover:underline focus:outline-none"
                    type="button"
                  >
                    Remove image
                  </button>
                )}
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="price" value="Price * (in INR)" className="mb-1" />
              <TextInput
                id="price"
                name="price"
                type="number"
                value={itemData.price || ''}
                onChange={handleInputChange}
                required
                disabled={isSubmitting}
              />
            </div>

            <div>
              <Label value="Food Type *" className="mb-1" />
              <div className="flex gap-4 mt-2">
                <div className="flex items-center">
                  <input
                    type="radio"
                    id="foodTypeVeg"
                    name="foodType"
                    checked={selectedFoodType === 'veg'}
                    onChange={() => handleFoodTypeChange('veg')}
                    className="mr-2"
                    disabled={isSubmitting}
                  />
                  <Label htmlFor="foodTypeVeg" className="text-green-600 font-medium">Veg</Label>
                </div>
                <div className="flex items-center">
                  <input
                    type="radio"
                    id="foodTypeNonVeg"
                    name="foodType"
                    checked={selectedFoodType === 'non-veg'}
                    onChange={() => handleFoodTypeChange('non-veg')}
                    className="mr-2"
                    disabled={isSubmitting}
                  />
                  <Label htmlFor="foodTypeNonVeg" className="text-red-600 font-medium">Non-veg</Label>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-4">
            {!isOtherSelected && (
              <div>
                <Label htmlFor="category" value="Category *" className="mb-1" />
                <Select
                  id="category"
                  name="category"
                  value={itemData.category || ''}
                  onChange={handleCategoryChange}
                  disabled={isSubmitting}
                >
                  {!isEditMode && (
                    <option value="Select" disabled>
                      Select
                    </option>
                  )}
                  {categories.length > 0 ? (
                    categories.map((category, index) => (
                      <option key={index} value={category}>
                        {category}
                      </option>
                    ))
                  ) : (
                    <option disabled>Loading categories...</option>
                  )}
                  <option value="Other">Other</option>
                </Select>
              </div>
            )}

            {isOtherSelected && (
              <div>
                <Label htmlFor="customCategory" value="Custom Category *" className="mb-1" />
                <TextInput
                  id="customCategory"
                  name="category"
                  placeholder="Enter custom category"
                  value={itemData.category || ''}
                  onChange={handleInputChange}
                  required
                  disabled={isSubmitting}
                />
              </div>
            )}
          </div>
        </div>

        <div className="flex flex-row justify-center items-center mt-6">
          
          <Button
            onClick={handleSubmit}
            className={`w-full sm:w-auto ${
              isFormValid() ? 'bg-primaryColor hover:bg-primaryColor/90' : 'bg-gray-400'
            }`}
            disabled={descriptionError || uploading || isSubmitting || !isFormValid()}
          >
          {isSubmitting ? (
    <>
     
      {isEditMode ? 'Saving...' : 'Submitting...'}
    </>
  ) : (
    isEditMode ? 'Save Changes' : 'Add Item'
  )}
          </Button>
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default ItemModal;